import * as React from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "motion/react"
import {
  Calendar,
  Trophy,
  Zap,
  Target,
  ChevronLeft,
  ChevronRight,
  Crown,
  Gift,
  Users,
  TrendingUp,
  X,
  Ticket,
  Swords,
  CircleDot,
  Music,
  Sparkles,
  Flame,
  Clock,
  DollarSign,
  Star,
  PartyPopper,
} from "lucide-react"
import { useAuth, type LeaderboardEntry, type AgencyStats, type WeeklyRaffleStatus, type GameEventWithProgress } from "@/contexts/AuthContext"
import { getEventIcon } from "@/lib/iconMap"
import { GameEventDetailModal } from "@/components/dashboard/GameEventDetailModal"
import { MusicPlayer } from "@/components/MusicPlayer"
import { useIsMobile } from "@/hooks/use-mobile"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const RIGHT_SIDEBAR_WIDTH = "18rem"
const RIGHT_SIDEBAR_COLLAPSED_WIDTH = "2.5rem"
const STORAGE_KEY = "right_sidebar_open"

// --- Ambient Floating Orbs ---
function AmbientOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {[
        { color: "#F37020", size: 80, top: "8%", left: "70%", duration: 12, delay: 0 },
        { color: "#9B59B6", size: 60, top: "30%", left: "15%", duration: 15, delay: 2 },
        { color: "#32CD32", size: 50, top: "55%", left: "80%", duration: 18, delay: 4 },
        { color: "#FFD700", size: 70, top: "75%", left: "25%", duration: 14, delay: 1 },
        { color: "#FF6B9D", size: 40, top: "90%", left: "60%", duration: 16, delay: 3 },
      ].map((orb, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: orb.size,
            height: orb.size,
            top: orb.top,
            left: orb.left,
            background: `radial-gradient(circle, ${orb.color}12 0%, transparent 70%)`,
            filter: "blur(20px)",
          }}
          animate={{
            y: [0, -20, 5, -15, 0],
            x: [0, 10, -8, 12, 0],
            scale: [1, 1.15, 0.95, 1.1, 1],
          }}
          transition={{
            duration: orb.duration,
            repeat: Infinity,
            ease: "easeInOut",
            delay: orb.delay,
          }}
        />
      ))}
    </div>
  )
}

// --- Shared UI Primitives ---

function SectionHeader({
  icon: Icon,
  iconBg,
  title,
  iconColor = "text-white",
}: {
  icon: React.ElementType
  iconBg: string
  title: string
  iconColor?: string
}) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <motion.div
        className="w-8 h-8 rounded-xl flex items-center justify-center border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)] relative overflow-hidden"
        style={{ backgroundColor: iconBg }}
        animate={{
          boxShadow: [
            `0 0 6px ${iconBg}30`,
            `0 0 14px ${iconBg}50`,
            `0 0 6px ${iconBg}30`,
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <Icon className={`w-4 h-4 ${iconColor} relative z-10`} />
        {/* Inner shimmer */}
        <motion.div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)" }}
          animate={{ x: ["-100%", "200%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
        />
      </motion.div>
      <h3
        className="font-bold text-sm text-white tracking-tight"
        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        {title}
      </h3>
    </div>
  )
}

function RetroCard({
  children,
  className = "",
  hover = false,
  glowColor,
}: {
  children: React.ReactNode
  className?: string
  hover?: boolean
  glowColor?: string
}) {
  return (
    <motion.div
      className={`bg-[#1C1C32] rounded-2xl border-[2.5px] border-[#2A2A45] shadow-[0_0_8px_rgba(243,112,32,0.12)] relative overflow-hidden ${
        hover ? "hover:shadow-[0_0_12px_rgba(243,112,32,0.15)] hover:-translate-y-0.5 transition-all duration-150" : ""
      } ${className}`}
      whileHover={hover ? { y: -2, scale: 1.01 } : undefined}
    >
      {glowColor && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at 50% 0%, ${glowColor}08 0%, transparent 70%)` }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      <div className="relative z-10">{children}</div>
    </motion.div>
  )
}

function RetroDivider() {
  return (
    <div className="flex items-center gap-2 py-1 relative">
      <div className="flex-1 h-[2px] rounded-full overflow-hidden bg-[#2A2A45]/30">
        <motion.div
          className="h-full w-1/3 rounded-full"
          style={{ background: "linear-gradient(90deg, transparent, #F37020, transparent)" }}
          animate={{ x: ["-100%", "400%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      <motion.div
        animate={{ rotate: [0, 180, 360], scale: [1, 1.2, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <CircleDot className="w-3 h-3 text-[#F37020]/25" />
      </motion.div>
      <div className="flex-1 h-[2px] rounded-full overflow-hidden bg-[#2A2A45]/30">
        <motion.div
          className="h-full w-1/3 rounded-full"
          style={{ background: "linear-gradient(90deg, transparent, #9B59B6, transparent)" }}
          animate={{ x: ["400%", "-100%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
    </div>
  )
}

// --- Animated Counter ---
function AnimatedCounter({ value, className }: { value: number; className?: string }) {
  const [displayValue, setDisplayValue] = React.useState(0)

  React.useEffect(() => {
    if (value === 0) {
      setDisplayValue(0)
      return
    }
    let start = 0
    const duration = 800
    const stepTime = 16
    const steps = duration / stepTime
    const increment = value / steps

    const timer = setInterval(() => {
      start += increment
      if (start >= value) {
        setDisplayValue(value)
        clearInterval(timer)
      } else {
        setDisplayValue(Math.floor(start))
      }
    }, stepTime)

    return () => clearInterval(timer)
  }, [value])

  return <span className={className}>{displayValue}</span>
}

// --- Live Pulse Dot ---
function LivePulse({ color = "#32CD32", size = 6 }: { color?: string; size?: number }) {
  return (
    <span className="relative inline-flex" style={{ width: size, height: size }}>
      <motion.span
        className="absolute inset-0 rounded-full"
        style={{ backgroundColor: color, opacity: 0.4 }}
        animate={{ scale: [1, 2.5, 1], opacity: [0.4, 0, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
      />
      <span className="relative inline-flex rounded-full w-full h-full" style={{ backgroundColor: color }} />
    </span>
  )
}

// --- Section Components ---

function FeaturedEventBanner({ event, onClickEvent }: { event: GameEventWithProgress | null; onClickEvent?: (id: string) => void }) {
  if (!event) return null

  const isComingSoon = event.status === "coming_soon"
  const isLive = event.status === "live"
  const hasImage = !!event.imageUrl

  return (
    <motion.button
      type="button"
      onClick={() => onClickEvent?.(event._id)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
      className="relative rounded-2xl border-[3px] border-[#2A2A45] shadow-[0_0_12px_rgba(243,112,32,0.15)] overflow-hidden group w-full text-left cursor-pointer"
      aria-label="View featured event"
      style={hasImage ? {
        backgroundImage: `url(${event.imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: 0,
        paddingBottom: "159.375%",
      } : undefined}
      whileHover={{ scale: 1.01 }}
    >
      {/* Fallback gradient if no image */}
      {!hasImage && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A30] via-[#2A1A40] to-[#1A1A30]" style={{ paddingBottom: "159.375%" }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <Crown className="w-16 h-16 text-[#FFD700]/20" />
          </div>
        </div>
      )}

      {/* Scanline overlay for CRT effect */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04] z-10"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.4) 2px, rgba(0,0,0,0.4) 4px)",
        }}
      />

      {/* Animated border glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-10 rounded-2xl"
        animate={{
          boxShadow: [
            "inset 0 0 20px rgba(255,215,0,0)",
            "inset 0 0 30px rgba(255,215,0,0.12)",
            "inset 0 0 20px rgba(255,215,0,0)",
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating sparkles */}
      {[
        { top: "12%", left: "15%", delay: 0 },
        { top: "25%", left: "78%", delay: 1.5 },
        { top: "45%", left: "55%", delay: 3 },
      ].map((spark, i) => (
        <motion.div
          key={i}
          className="absolute z-20"
          style={{ top: spark.top, left: spark.left }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0.5, 1, 0.5],
            y: [0, -8, 0],
          }}
          transition={{ duration: 3, repeat: Infinity, delay: spark.delay, ease: "easeInOut" }}
        >
          <Sparkles className="w-3 h-3 text-[#FFD700]/50" />
        </motion.div>
      ))}

      {/* Bottom overlay */}
      <div className="absolute bottom-0 inset-x-0 z-20 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-3.5 pb-3.5 pt-16">
        <div className="flex items-center gap-2 mb-1.5">
          {isComingSoon && (
            <motion.div
              className="flex items-center gap-1.5 bg-[#FFD700] text-black px-2.5 py-1 rounded-lg border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)]"
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Crown className="w-3 h-3" />
              </motion.div>
              <span
                className="text-[10px] font-black uppercase tracking-wider"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                Coming Soon
              </span>
            </motion.div>
          )}
          {isLive && (
            <motion.div
              className="flex items-center gap-1.5 bg-red-500 text-white px-2.5 py-1 rounded-lg border-2 border-[#2A2A45]"
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span
                className="text-[10px] font-black uppercase tracking-wider"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                Live Now
              </span>
            </motion.div>
          )}
        </div>
        <p
          className="text-white font-bold text-xs leading-snug mb-0.5"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          {event.title}
        </p>
        <p
          className="text-white/80 text-[11px] font-medium leading-snug"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          {event.description?.slice(0, 80)}{event.description && event.description.length > 80 ? "..." : ""}
        </p>
      </div>
    </motion.button>
  )
}

// --- Countdown Hook ---
function useCountdown(targetDate: string | undefined) {
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  React.useEffect(() => {
    if (!targetDate) return
    const target = new Date(targetDate).getTime()

    const tick = () => {
      const now = Date.now()
      const diff = Math.max(0, target - now)
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      })
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [targetDate])

  return timeLeft
}

// --- Countdown Digit ---
function CountdownDigit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <motion.div
        className="w-12 h-14 bg-[#1A1A30] rounded-xl border-2 border-[#2A2A45] flex items-center justify-center shadow-[0_0_8px_rgba(155,89,182,0.15)]"
        key={value}
        initial={{ rotateX: -30, opacity: 0.5 }}
        animate={{ rotateX: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <span
          className="text-2xl font-black text-white tabular-nums"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          {String(value).padStart(2, "0")}
        </span>
      </motion.div>
      <span
        className="text-[9px] font-bold text-[#6A6A8A] uppercase tracking-wider"
        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        {label}
      </span>
    </div>
  )
}

// --- Prize Tier Config ---
const PRIZE_TIER_CONFIG: Record<string, { color: string; bg: string; icon: React.ElementType; label: string }> = {
  "weekly-grand": { color: "#FFD700", bg: "linear-gradient(135deg, #FFD700, #FFA500)", icon: Crown, label: "Grand Prize" },
  "weekly-tier-2": { color: "#C0C0C0", bg: "linear-gradient(135deg, #C0C0C0, #A0A0A0)", icon: Trophy, label: "Tier 2" },
  "weekly-tier-3": { color: "#CD7F32", bg: "linear-gradient(135deg, #CD7F32, #8B5A2B)", icon: Star, label: "Tier 3" },
  "weekly-tier-4": { color: "#9B59B6", bg: "linear-gradient(135deg, #9B59B6, #7D3C98)", icon: Gift, label: "Tier 4" },
}

// --- Weekly Raffle Modal ---
function WeeklyRaffleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { getWeeklyRaffleStatus, user } = useAuth()
  const [data, setData] = React.useState<WeeklyRaffleStatus | null>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setLoading(true)
    getWeeklyRaffleStatus().then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [open])

  const countdownTarget = data?.status === "pending" ? data.drawTime : data?.nextDrawTime
  const countdown = useCountdown(countdownTarget)

  // Close on escape
  React.useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [open, onClose])

  const currentUserWon = data?.winners?.find((w) => w.isCurrentUser)

  // Group winners by prize tier
  const winnersByTier = React.useMemo(() => {
    if (!data?.winners) return []
    const tiers = new Map<string, typeof data.winners>()
    for (const w of data.winners) {
      const existing = tiers.get(w.prizeLabel) || []
      existing.push(w)
      tiers.set(w.prizeLabel, existing)
    }
    return Array.from(tiers.entries())
  }, [data?.winners])

  const modalContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Card */}
          <motion.div
            className="relative w-full max-w-md max-h-[85vh] bg-[#161628] rounded-3xl border-[3px] border-[#2A2A45] shadow-[0_0_40px_rgba(155,89,182,0.2)] overflow-hidden flex flex-col"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", bounce: 0.25, duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-8 h-8 rounded-xl bg-[#1C1C32] border-2 border-[#2A2A45] flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-4 h-4 text-white/60" />
            </motion.button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-5">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <motion.div
                    className="w-12 h-12 rounded-full border-3 border-[#9B59B6] border-t-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <span className="text-sm text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                    Loading raffle...
                  </span>
                </div>
              ) : data?.status === "drawn" ? (
                /* ===== DRAWN STATE ===== */
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="text-center">
                    <motion.div
                      className="flex items-center justify-center gap-2 mb-2"
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <motion.div
                        animate={{ rotate: [-10, 10, -10] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <PartyPopper className="w-6 h-6 text-[#FFD700]" />
                      </motion.div>
                      <h2
                        className="text-xl font-black text-white"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        This Week's Winners!
                      </h2>
                      <motion.div
                        animate={{ rotate: [10, -10, 10] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <PartyPopper className="w-6 h-6 text-[#FFD700] scale-x-[-1]" />
                      </motion.div>
                    </motion.div>

                    {/* Floating sparkles */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute"
                        style={{ top: `${10 + i * 8}%`, left: `${15 + i * 17}%` }}
                        animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5], y: [0, -6, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
                      >
                        <Sparkles className="w-3 h-3 text-[#FFD700]/40" />
                      </motion.div>
                    ))}
                  </div>

                  {/* Current User Won Banner */}
                  {currentUserWon && (
                    <motion.div
                      className="relative rounded-2xl border-2 border-[#FFD700]/50 p-4 text-center overflow-hidden"
                      style={{ background: "linear-gradient(135deg, rgba(255,215,0,0.12), rgba(243,112,32,0.08))" }}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.3 }}
                    >
                      <motion.div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(255,215,0,0.08) 0%, transparent 70%)" }}
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <div className="relative z-10">
                        <motion.p
                          className="text-2xl font-black text-[#FFD700] mb-1"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                          animate={{ textShadow: ["0 0 8px rgba(255,215,0,0)", "0 0 16px rgba(255,215,0,0.5)", "0 0 8px rgba(255,215,0,0)"] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                          You Won ${currentUserWon.cashUsd}!
                        </motion.p>
                        <p className="text-xs text-white/60 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                          Added to your pending balance
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Winner Tiers */}
                  <div className="flex flex-col gap-2.5">
                    {winnersByTier.map(([prizeLabel, winners], tierIndex) => {
                      const tier = PRIZE_TIER_CONFIG[prizeLabel] || PRIZE_TIER_CONFIG["weekly-tier-4"]!
                      const TierIcon = tier.icon
                      return (
                        <motion.div
                          key={prizeLabel}
                          className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5 relative overflow-hidden"
                          initial={{ x: 20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.15 + tierIndex * 0.1, type: "spring", bounce: 0.2 }}
                        >
                          {/* Tier glow */}
                          <motion.div
                            className="absolute inset-0 pointer-events-none"
                            style={{ background: `radial-gradient(ellipse at 0% 50%, ${tier.color}08 0%, transparent 60%)` }}
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          />
                          <div className="relative z-10">
                            {/* Tier header */}
                            <div className="flex items-center gap-2.5 mb-2.5">
                              <div
                                className="w-8 h-8 rounded-xl flex items-center justify-center border-2 border-[#2A2A45]"
                                style={{ background: tier.bg }}
                              >
                                <TierIcon className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1">
                                <span
                                  className="text-xs font-black text-white"
                                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                                >
                                  {tier.label}
                                </span>
                                <span className="text-xs font-black ml-2" style={{ color: tier.color, fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                                  ${winners[0]?.cashUsd.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            {/* Winners list */}
                            <div className="flex flex-col gap-1">
                              {winners.map((w, wi) => (
                                <div
                                  key={wi}
                                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${
                                    w.isCurrentUser
                                      ? "bg-[#F37020]/10 border border-[#F37020]/30"
                                      : "bg-[#1A1A30]/50"
                                  }`}
                                >
                                  <div
                                    className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0"
                                    style={{ background: tier.bg }}
                                  >
                                    {wi + 1}
                                  </div>
                                  <span
                                    className={`text-xs font-bold truncate ${
                                      w.isCurrentUser ? "text-[#F37020]" : "text-white/80"
                                    }`}
                                    style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                                  >
                                    {w.displayName}
                                    {w.isCurrentUser && " (You)"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>

                  {/* Footer */}
                  <div className="text-center space-y-2 pt-1">
                    <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                      {data.totalQualified} total participants
                    </p>
                    <div className="bg-[#1C1C32] rounded-xl border-2 border-[#2A2A45] p-3">
                      <p className="text-[10px] text-[#9A9ABF] font-bold mb-2" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                        Next draw in
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <CountdownDigit value={countdown.days} label="Days" />
                        <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                        <CountdownDigit value={countdown.hours} label="Hrs" />
                        <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                        <CountdownDigit value={countdown.minutes} label="Min" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ===== PENDING STATE ===== */
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="text-center">
                    <motion.div
                      className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center border-2 border-[#2A2A45]"
                      style={{ background: "linear-gradient(135deg, #9B59B6, #7D3C98)" }}
                      animate={{
                        boxShadow: [
                          "0 0 12px rgba(155,89,182,0.2)",
                          "0 0 24px rgba(155,89,182,0.4)",
                          "0 0 12px rgba(155,89,182,0.2)",
                        ],
                      }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Ticket className="w-7 h-7 text-white" />
                    </motion.div>
                    <h2
                      className="text-xl font-black text-white mb-1"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      Weekly Cash Raffle
                    </h2>
                    <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                      Submit 5 deals this week to qualify
                    </p>
                  </div>

                  {/* Countdown */}
                  <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-4">
                    <p
                      className="text-[10px] text-[#9A9ABF] font-bold text-center mb-3 uppercase tracking-wider"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      Draw in
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <CountdownDigit value={countdown.days} label="Days" />
                      <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                      <CountdownDigit value={countdown.hours} label="Hrs" />
                      <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                      <CountdownDigit value={countdown.minutes} label="Min" />
                      <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                      <CountdownDigit value={countdown.seconds} label="Sec" />
                    </div>
                  </div>

                  {/* Qualified Badge */}
                  <div className="flex items-center justify-center gap-2">
                    <motion.div
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 ${
                        data?.currentUserQualified
                          ? "bg-[#32CD32]/15 border-[#32CD32]/40 text-[#32CD32]"
                          : "bg-[#1A1A30] border-[#2A2A45] text-[#6A6A8A]"
                      }`}
                      animate={data?.currentUserQualified ? { scale: [1, 1.05, 1] } : undefined}
                      transition={data?.currentUserQualified ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : undefined}
                    >
                      {data?.currentUserQualified ? (
                        <Sparkles className="w-3.5 h-3.5" />
                      ) : (
                        <Clock className="w-3.5 h-3.5" />
                      )}
                      <span
                        className="text-xs font-bold"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {data?.currentUserQualified ? "You're Qualified!" : "Not Yet Qualified"}
                      </span>
                    </motion.div>
                    <div className="flex items-center gap-1 px-2.5 py-1.5 bg-[#1A1A30] rounded-xl border-2 border-[#2A2A45]">
                      <Users className="w-3.5 h-3.5 text-[#9B59B6]" />
                      <span
                        className="text-xs font-bold text-white"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {data?.totalQualified ?? 0}
                      </span>
                    </div>
                  </div>

                  {/* Prize Pool Preview */}
                  <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5">
                    <p
                      className="text-[10px] text-[#9A9ABF] font-bold mb-2.5 uppercase tracking-wider"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      Prize Pool
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: "Grand", amount: "$3,000", winners: "1 winner", color: "#FFD700" },
                        { label: "Tier 2", amount: "$1,500", winners: "2 winners", color: "#C0C0C0" },
                        { label: "Tier 3", amount: "$1,000", winners: "4 winners", color: "#CD7F32" },
                        { label: "Tier 4", amount: "$200", winners: "10 winners", color: "#9B59B6" },
                      ].map((prize, i) => (
                        <motion.div
                          key={prize.label}
                          className="bg-[#1A1A30] rounded-xl border border-[#2A2A45]/60 p-2.5 text-center"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 + i * 0.05 }}
                        >
                          <DollarSign className="w-4 h-4 mx-auto mb-1" style={{ color: prize.color }} />
                          <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            {prize.amount}
                          </p>
                          <p className="text-[9px] text-[#6A6A8A] font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            {prize.winners}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Qualified Users List */}
                  {(data?.qualifiedUsers?.length ?? 0) > 0 && (
                    <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5">
                      <p
                        className="text-[10px] text-[#9A9ABF] font-bold mb-2.5 uppercase tracking-wider"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        Qualified Players
                      </p>
                      <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
                        {data!.qualifiedUsers!.map((name, i) => {
                          const isCurrentUser = name === user?.onboardingData?.displayName
                          return (
                            <motion.div
                              key={i}
                              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${
                                isCurrentUser ? "bg-[#F37020]/10 border border-[#F37020]/30" : "bg-[#1A1A30]/50"
                              }`}
                              initial={{ opacity: 0, x: 8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.03 }}
                            >
                              <div className="w-5 h-5 rounded-full bg-[#9B59B6]/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-[8px] font-black text-[#9B59B6]">{i + 1}</span>
                              </div>
                              <span
                                className={`text-xs font-bold truncate ${isCurrentUser ? "text-[#F37020]" : "text-white/70"}`}
                                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                              >
                                {name}{isCurrentUser && " (You)"}
                              </span>
                            </motion.div>
                          )
                        })}
                      </div>
                      {(data?.totalQualified ?? 0) > 20 && (
                        <p className="text-[9px] text-[#6A6A8A] mt-2 text-center font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                          +{(data!.totalQualified) - 20} more qualified
                        </p>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <p className="text-[10px] text-[#6A6A8A] text-center font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                    Draw happens every Friday at 5 PM ET
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  if (typeof document === "undefined") return null
  return createPortal(modalContent, document.body)
}

function WeeklyRaffleProgress() {
  const { user } = useAuth()
  const [showRaffleModal, setShowRaffleModal] = React.useState(false)
  const weeklyDeals = user?.points?.weeklyDeals ?? 0
  const target = 5
  const progress = Math.min(weeklyDeals / target, 1)
  const remaining = Math.max(target - weeklyDeals, 0)
  const qualified = progress >= 1

  return (
    <div>
      <SectionHeader icon={Ticket} iconBg="#9B59B6" title="Weekly Raffle" />
      <div
        className="cursor-pointer"
        onClick={() => setShowRaffleModal(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowRaffleModal(true) }}
      >
        <RetroCard className="p-3.5" glowColor={qualified ? "#32CD32" : "#9B59B6"} hover>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-baseline gap-1">
              <motion.span
                className="text-2xl font-black text-white"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                key={weeklyDeals}
                initial={{ scale: 1.3, color: "#FFD700" }}
                animate={{ scale: 1, color: "#FFFFFF" }}
                transition={{ duration: 0.4 }}
              >
                {weeklyDeals}
              </motion.span>
              <span className="text-xs font-bold text-[#6A6A8A]">/ {target} deals</span>
            </div>
            <motion.span
              className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border-2 ${
                qualified
                  ? "bg-[#32CD32] text-white border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)]"
                  : "bg-[#1A1A30] text-[#9A9ABF] border-[#2A2A45]"
              }`}
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              animate={qualified ? { scale: [1, 1.08, 1] } : undefined}
              transition={qualified ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : undefined}
            >
              {qualified ? "Qualified!" : `${remaining} to go`}
            </motion.span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-4 bg-[#1A1A30] rounded-full border-2 border-[#2A2A45]/50 overflow-hidden relative">
            <motion.div
              className="h-full rounded-full relative overflow-hidden"
              style={{
                background: qualified
                  ? "linear-gradient(90deg, #32CD32, #7FFF00)"
                  : "linear-gradient(90deg, #F37020, #FFD700)",
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            >
              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
                }}
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
              {/* Glow pulse on the edge */}
              <motion.div
                className="absolute right-0 top-0 h-full w-3"
                style={{
                  background: qualified
                    ? "radial-gradient(circle at right, #7FFF00, transparent)"
                    : "radial-gradient(circle at right, #FFD700, transparent)",
                }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>

            {/* Progress dots */}
            {[...Array(target)].map((_, i) => (
              <div
                key={i}
                className="absolute top-1/2 -translate-y-1/2 w-0.5 h-2.5 bg-black/10 rounded-full"
                style={{ left: `${((i + 1) / target) * 100}%` }}
              />
            ))}
          </div>

          <p
            className="text-[10px] text-[#6A6A8A] mt-2 font-medium leading-snug"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            {qualified
              ? "You\u2019re in this week\u2019s Cash Raffle!"
              : `${remaining} more deal${remaining !== 1 ? "s" : ""} to qualify for Weekly Raffle`}
          </p>
        </RetroCard>
      </div>
      <WeeklyRaffleModal open={showRaffleModal} onClose={() => setShowRaffleModal(false)} />
    </div>
  )
}

function SideQuestsSection() {
  const { user } = useAuth()
  const activeQuests = (user?.sideQuests ?? []).filter((q: any) => q.status === "active")

  if (activeQuests.length === 0) return null

  const tierConfig: Record<string, { color: string; bg: string; label: string }> = {
    easy: { color: "#32CD32", bg: "#32CD3220", label: "Easy" },
    medium: { color: "#FFD700", bg: "#FFD70020", label: "Medium" },
    hard: { color: "#F37020", bg: "#F3702020", label: "Hard" },
  }

  return (
    <div>
      <SectionHeader icon={Swords} iconBg="#FF6B9D" title="Side Quests" />
      <div className="space-y-2">
        {activeQuests.map((quest: any, qi: number) => {
          const tier = tierConfig[quest.tier] ?? tierConfig.easy!
          const progress = Math.min(quest.progress / quest.target, 1)
          const deadline = new Date(quest.deadline)
          const now = new Date()
          const hoursLeft = Math.max(0, Math.round((deadline.getTime() - now.getTime()) / (1000 * 60 * 60)))
          const timeLabel = hoursLeft >= 24 ? `${Math.floor(hoursLeft / 24)}d ${hoursLeft % 24}h` : `${hoursLeft}h`
          const isUrgent = hoursLeft <= 6

          return (
            <motion.div
              key={quest.questId}
              initial={{ opacity: 0, x: 15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: qi * 0.1, type: "spring", bounce: 0.2 }}
            >
              <RetroCard className="p-3" glowColor={tier.color}>
                <div className="flex items-center justify-between mb-2">
                  <motion.span
                    className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border"
                    style={{ color: tier.color, backgroundColor: tier.bg, borderColor: `${tier.color}40` }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: qi * 0.5 }}
                  >
                    {tier.label}
                  </motion.span>
                  <motion.span
                    className="text-xs font-black"
                    style={{ color: tier.color, fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    animate={{ textShadow: [`0 0 4px ${tier.color}00`, `0 0 8px ${tier.color}60`, `0 0 4px ${tier.color}00`] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    ${quest.cashPrize}
                  </motion.span>
                </div>

                <p
                  className="text-[11px] font-bold text-white mb-2"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {quest.label}
                </p>

                {/* Progress bar */}
                <div className="w-full h-3 bg-[#1A1A30] rounded-full border border-[#2A2A45]/50 overflow-hidden relative mb-1.5">
                  <motion.div
                    className="h-full rounded-full relative overflow-hidden"
                    style={{ background: `linear-gradient(90deg, ${tier.color}, ${tier.color}CC)` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress * 100}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    <motion.div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)" }}
                      animate={{ x: ["-100%", "200%"] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </motion.div>
                </div>

                <div className="flex items-center justify-between">
                  <span
                    className="text-[10px] font-bold text-[#9A9ABF]"
                    style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                  >
                    {quest.progress}/{quest.target} deals
                  </span>
                  <span className="flex items-center gap-1">
                    {isUrgent && <LivePulse color="#EF4444" size={5} />}
                    <span
                      className={`text-[10px] font-bold ${isUrgent ? "text-red-400" : "text-[#6A6A8A]"}`}
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      {timeLabel} left
                    </span>
                  </span>
                </div>
              </RetroCard>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

function AgencyActivity() {
  const { user, getAgencyStats } = useAuth()
  const [stats, setStats] = React.useState<AgencyStats | null>(null)
  const [loading, setLoading] = React.useState(true)

  const agencyId = user?.generatedAgencyId || user?.onboardingData?.agencyId

  React.useEffect(() => {
    if (!agencyId) {
      setLoading(false)
      return
    }
    let cancelled = false

    const fetchStats = () => {
      getAgencyStats(agencyId).then((result) => {
        if (!cancelled && result.success && result.stats) {
          setStats(result.stats)
        }
        if (!cancelled) setLoading(false)
      })
    }

    fetchStats()
    const interval = setInterval(fetchStats, 30000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [agencyId])

  if (!agencyId) return null

  const statItems = stats
    ? [
        {
          icon: TrendingUp,
          value: stats.dealsToday,
          label: "Today",
          color: "#F37020",
        },
        {
          icon: Calendar,
          value: stats.dealsThisWeek,
          label: "This Week",
          color: "#9B59B6",
        },
        {
          icon: Users,
          value: stats.memberCount,
          label: "Members",
          color: "#32CD32",
        },
      ]
    : []

  return (
    <div>
      <SectionHeader icon={Zap} iconBg="#32CD32" title="Today's Activity" />

      {loading ? (
        <div className="grid grid-cols-3 gap-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-20 bg-[#1C1C32]/60 rounded-2xl border-2 border-[#2A2A45]/40 animate-pulse"
            />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-3 gap-2">
          {statItems.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, type: "spring", bounce: 0.3 }}
            >
              <RetroCard className="p-2.5 text-center" hover glowColor={item.color}>
                <motion.div
                  className="w-7 h-7 mx-auto mb-1.5 rounded-lg flex items-center justify-center border-2 border-[#2A2A45] shadow-none relative"
                  style={{ backgroundColor: `${item.color}20` }}
                  animate={{ rotate: [0, 3, -3, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }}
                >
                  <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                </motion.div>
                <AnimatedCounter
                  value={item.value}
                  className="text-xl font-black text-white block leading-none"
                />
                <p
                  className="text-[9px] font-bold text-[#6A6A8A] mt-1 uppercase tracking-wider"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {item.label}
                </p>
              </RetroCard>
            </motion.div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function MiniLeaderboard() {
  const { getLeaderboard } = useAuth()
  const [entries, setEntries] = React.useState<LeaderboardEntry[]>([])
  const [mode, setMode] = React.useState<"agency" | "agent">("agent")
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false

    const fetchLeaderboard = (showLoader = true) => {
      if (showLoader) setLoading(true)
      getLeaderboard(mode).then((result) => {
        if (!cancelled && result.success && result.data) {
          setEntries(result.data.leaderboard.slice(0, 5))
        }
        if (!cancelled) setLoading(false)
      })
    }

    fetchLeaderboard(true)
    const interval = setInterval(() => fetchLeaderboard(false), 30000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [mode])

  const getRankDecoration = (rank: number) => {
    switch (rank) {
      case 1:
        return {
          bg: "bg-gradient-to-br from-[#FFD700] to-[#FFA500]",
          border: "border-[#2A2A45]",
          text: "text-black",
          shadow: "shadow-[0_0_6px_rgba(243,112,32,0.1)]",
          glow: "#FFD700",
        }
      case 2:
        return {
          bg: "bg-gradient-to-br from-gray-300 to-gray-400",
          border: "border-[#2A2A45]",
          text: "text-black",
          shadow: "shadow-none",
          glow: "#C0C0C0",
        }
      case 3:
        return {
          bg: "bg-gradient-to-br from-amber-600 to-amber-700",
          border: "border-[#2A2A45]",
          text: "text-white",
          shadow: "shadow-none",
          glow: "#CD7F32",
        }
      default:
        return {
          bg: "bg-[#1A1A30]",
          border: "border-[#2A2A45]",
          text: "text-[#6A6A8A]",
          shadow: "",
          glow: undefined,
        }
    }
  }

  return (
    <div>
      <SectionHeader icon={Trophy} iconBg="#FFD700" title="Daily Top Performers" iconColor="text-black" />

      {/* Mode Toggle */}
      <div className="flex gap-1 mb-3 bg-[#222240] rounded-xl p-1 border-2 border-[#2A2A45]/40">
        {(["agent", "agency"] as const).map((m) => (
          <motion.button
            key={m}
            onClick={() => setMode(m)}
            className={`flex-1 text-[10px] font-bold py-1.5 rounded-lg transition-all ${
              mode === m
                ? "bg-[#1C1C32] border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)] text-white"
                : "text-[#6A6A8A] hover:text-[#9A9ABF] border-2 border-transparent"
            }`}
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            whileTap={{ scale: 0.95 }}
          >
            {m === "agent" ? "Agents" : "Agencies"}
          </motion.button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-10 bg-[#1C1C32]/60 rounded-xl border-2 border-[#2A2A45]/30 animate-pulse"
            />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <RetroCard className="py-6 text-center">
          <Trophy className="w-6 h-6 text-[#6A6A8A] mx-auto mb-2" />
          <p
            className="text-xs text-[#6A6A8A] font-medium"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            No data yet
          </p>
        </RetroCard>
      ) : (
        <div className="space-y-1.5">
          {entries.map((entry, i) => {
            const rank = getRankDecoration(entry.rank)
            return (
              <motion.div
                key={`${entry.rank}-${entry.name}`}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06, type: "spring", bounce: 0.2 }}
              >
                <RetroCard
                  className={`px-2.5 py-2 ${
                    entry.isCurrentUser ? "ring-2 ring-[#F37020]/40 bg-[#F37020]/5" : ""
                  }`}
                  glowColor={rank.glow}
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black border-2 ${rank.bg} ${rank.border} ${rank.text} ${rank.shadow} flex-shrink-0 relative`}
                      animate={entry.rank === 1 ? { scale: [1, 1.1, 1] } : undefined}
                      transition={entry.rank === 1 ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : undefined}
                    >
                      {entry.rank}
                      {entry.rank === 1 && (
                        <motion.div
                          className="absolute -top-1.5 -right-1.5"
                          animate={{ rotate: [0, 15, -15, 0], y: [0, -1, 0] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        >
                          <Crown className="w-3 h-3 text-[#FFD700]" />
                        </motion.div>
                      )}
                    </motion.div>
                    <span
                      className={`text-xs font-bold truncate flex-1 ${
                        entry.isCurrentUser ? "text-[#F37020]" : "text-white"
                      }`}
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      {entry.name}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span
                        className="text-[10px] font-black text-white"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {entry.deals}
                      </span>
                      <span className="text-[9px] text-[#6A6A8A] font-medium">deals</span>
                    </div>
                  </div>
                </RetroCard>
              </motion.div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function EventsSection({ events, onClickEvent }: { events: GameEventWithProgress[]; onClickEvent?: (id: string) => void }) {
  if (events.length === 0) return null

  return (
    <div>
      <SectionHeader icon={Calendar} iconBg="#F37020" title="Events" />
      <div className="space-y-2">
        {events.map((event, i) => {
          const Icon = getEventIcon(event.iconName)
          const isLive = event.status === "live"
          const dateLabel = event.startDate
            ? new Date(event.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
            : event.status === "coming_soon" ? "Coming Soon" : "Ongoing"

          return (
            <motion.div
              key={event._id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, type: "spring", bounce: 0.15 }}
              onClick={() => onClickEvent?.(event._id)}
              className="cursor-pointer"
            >
              <RetroCard className="p-2.5" hover>
                <div className="flex items-start gap-2.5">
                  <motion.div
                    className="w-8 h-8 rounded-xl flex items-center justify-center border-2 border-[#2A2A45] shadow-none flex-shrink-0 relative"
                    style={{ backgroundColor: `${event.badgeColor}18` }}
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                  >
                    <Icon className="w-4 h-4" style={{ color: event.badgeColor }} />
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span
                        className="font-bold text-[11px] text-white"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {event.title}
                      </span>
                      <motion.span
                        className="text-white text-[8px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 border border-[#2A2A45]/60"
                        style={{
                          backgroundColor: event.badgeColor,
                          fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                        animate={{ scale: [1, 1.06, 1] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                      >
                        {event.badge}
                      </motion.span>
                    </div>
                    <div className="flex items-center gap-1">
                      {isLive ? (
                        <>
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                          <p className="text-[10px] text-red-400 font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            LIVE
                          </p>
                        </>
                      ) : (
                        <>
                          <LivePulse color={event.badgeColor} size={4} />
                          <p className="text-[10px] text-[#6A6A8A] font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            {dateLabel}
                          </p>
                        </>
                      )}
                    </div>
                    <p
                      className="text-[10px] text-[#9A9ABF] mt-0.5 leading-snug font-medium"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      {event.description?.slice(0, 60)}{event.description && event.description.length > 60 ? "..." : ""}
                    </p>
                  </div>
                </div>
              </RetroCard>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

// --- Right Sidebar Content (shared between desktop and mobile) ---
function RightSidebarContent({ onOpenEvents }: { onOpenEvents?: () => void }) {
  const { getGameEvents } = useAuth()
  const [gameEvents, setGameEvents] = React.useState<GameEventWithProgress[]>([])
  const [featuredEvent, setFeaturedEvent] = React.useState<GameEventWithProgress | null>(null)
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null)

  React.useEffect(() => {
    getGameEvents().then((result) => {
      if (result.success) {
        setGameEvents(result.events || [])
        setFeaturedEvent(result.featuredEvent || null)
      }
    })
  }, [getGameEvents])

  const handleClickEvent = (id: string) => {
    setSelectedEventId(id)
  }

  return (
    <div className="flex flex-col gap-4 p-3.5 overflow-y-auto h-full touch-pan-y relative">
      <AmbientOrbs />

      <div className="relative z-10 flex flex-col gap-4">
        <MusicPlayer />

        <FeaturedEventBanner event={featuredEvent} onClickEvent={handleClickEvent} />

        <RetroDivider />

        <WeeklyRaffleProgress />

        <SideQuestsSection />

        <RetroDivider />

        <AgencyActivity />

        <RetroDivider />
        <MiniLeaderboard />

        <RetroDivider />

        <EventsSection events={gameEvents} onClickEvent={handleClickEvent} />

        {/* Bottom spacer */}
        <div className="h-4" />
      </div>

      <GameEventDetailModal
        open={!!selectedEventId}
        onClose={() => setSelectedEventId(null)}
        eventId={selectedEventId}
      />
    </div>
  )
}

// --- Main Export ---
interface RightSidebarProps {
  mobileOpen: boolean
  onMobileOpenChange: (open: boolean) => void
  onNavigateToEvents?: () => void
}

export function RightSidebar({ mobileOpen, onMobileOpenChange, onNavigateToEvents }: RightSidebarProps) {
  const isMobile = useIsMobile()
  const [desktopOpen, setDesktopOpen] = React.useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored !== null ? stored === "true" : true
  })

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(desktopOpen))
  }, [desktopOpen])

  // Mobile: use Sheet
  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side="right"
          className="bg-[#161628] p-0 w-[18rem] border-l-4 border-[#2A2A45] [&>button]:hidden"
          showCloseButton={false}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Activity Panel</SheetTitle>
            <SheetDescription>Shows events, leaderboard, and stats.</SheetDescription>
          </SheetHeader>
          {/* Close button header */}
          <div className="flex items-center justify-between px-3.5 py-3 border-b-[3px] border-[#2A2A45]/50">
            <div className="flex items-center gap-2">
              <motion.div
                className="w-7 h-7 rounded-lg bg-[#F37020] flex items-center justify-center border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)]"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <Swords className="w-3.5 h-3.5 text-white" />
              </motion.div>
              <span
                className="font-bold text-white text-sm"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                Activity
              </span>
            </div>
            <motion.button
              type="button"
              onClick={() => onMobileOpenChange(false)}
              className="w-8 h-8 rounded-xl bg-[#1C1C32] border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)] flex items-center justify-center"
              whileHover={{ boxShadow: "3px 3px 0 0 #1a1a1a", rotate: 90 }}
              whileTap={{ scale: 0.95, boxShadow: "1px 1px 0 0 #1a1a1a" }}
            >
              <X className="w-4 h-4" />
            </motion.button>
          </div>
          <RightSidebarContent
            onOpenEvents={() => {
              onNavigateToEvents?.()
              onMobileOpenChange(false)
            }}
          />
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop: fixed right panel
  return (
    <div
      className="hidden md:flex flex-shrink-0 relative"
      style={{
        width: desktopOpen ? RIGHT_SIDEBAR_WIDTH : RIGHT_SIDEBAR_COLLAPSED_WIDTH,
        transition: "width 200ms ease-in-out",
      }}
    >
      {/* Collapse toggle */}
      <motion.button
        onClick={() => setDesktopOpen((prev) => !prev)}
        className="absolute top-4 -left-3.5 z-20 w-7 h-7 rounded-full bg-[#1C1C32] border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)] flex items-center justify-center"
        whileHover={{ scale: 1.15, boxShadow: "3px 3px 0 0 #1a1a1a" }}
        whileTap={{ scale: 0.9, boxShadow: "1px 1px 0 0 #1a1a1a" }}
        animate={{
          boxShadow: [
            "0 0 6px rgba(243,112,32,0.1)",
            "0 0 12px rgba(243,112,32,0.25)",
            "0 0 6px rgba(243,112,32,0.1)",
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        title={desktopOpen ? "Collapse sidebar" : "Expand sidebar"}
      >
        <motion.div
          animate={{ x: desktopOpen ? [0, 2, 0] : [0, -2, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {desktopOpen ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5" />
          )}
        </motion.div>
      </motion.button>

      {/* Sidebar container */}
      <div
        className="fixed top-0 right-0 h-svh bg-[#161628] border-l-4 border-[#2A2A45] flex flex-col"
        style={{
          width: desktopOpen ? RIGHT_SIDEBAR_WIDTH : RIGHT_SIDEBAR_COLLAPSED_WIDTH,
          transition: "width 200ms ease-in-out",
        }}
      >
        {desktopOpen ? (
          <RightSidebarContent onOpenEvents={onNavigateToEvents} />
        ) : (
          <div className="flex flex-col items-center gap-4 pt-5">
            {[
              { icon: Music, bg: "#F37020", color: "text-white" },
              { icon: Zap, bg: "#F37020", color: "text-white" },
              { icon: Trophy, bg: "#FFD700", color: "text-black" },
              { icon: Target, bg: "#9B59B6", color: "text-white" },
              { icon: Calendar, bg: "#32CD32", color: "text-white" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05, type: "spring", bounce: 0.4 }}
                className={`w-7 h-7 rounded-lg flex items-center justify-center border-2 border-[#2A2A45]/70`}
                style={{ backgroundColor: item.bg }}
                whileHover={{ scale: 1.2, rotate: 10 }}
              >
                <item.icon className={`w-3.5 h-3.5 ${item.color}`} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
